import {Pipe} from "@angular/core";
import {KompaktdarstellungPipe} from "../kompakt-darstellung.pipe";
import {FormatConfigService} from "../../../core/services/format-config.service";
import {PlatformLocation} from "@angular/common";
import {ComponentLocationStrategy} from "../../services/location-strategy/component-location-strategy";
import {DecimalHochkommaPipe} from "../../pipes/simple/decimalHochkomma.pipe";
import {GebaeudeAufGrundstueckDetailEntryMeta} from "../../../core/av-api/model/gebaeudeAufGrundstueckDetailEntryMeta";
import {TranslateNamePipe} from "../../pipes/simple/translate-name.pipe";
import {GebaeudeVersionDetailEntryMeta} from "../../../core/av-api";
import {TranslateService} from "@ngx-translate/core";
import {keysToTranslate} from 'i18n/keys-to-translate';

@Pipe({
  name: 'kompaktGebaeudeMaster',
  pure: false
})
export class KompaktGebaeudeMasterPipe extends KompaktdarstellungPipe<GebaeudeVersionDetailEntryMeta> {

  constructor(protected formatConfigService: FormatConfigService,
              protected location: PlatformLocation,
              protected kompaktLocation: ComponentLocationStrategy,
              private hochkommaPipe: DecimalHochkommaPipe,
              private translateName: TranslateNamePipe,
              private translateService: TranslateService) {
    super(formatConfigService, 'GEBAEUDE', location, kompaktLocation);
    keysToTranslate<any>([
      "Einzelobjekt",
      "Gebäude/Bauten",
      "Gesamtfläche",
      "projektiert",
      "unterirdisch",
    ]);
  }

  transform(version: GebaeudeVersionDetailEntryMeta, gebaeudeAufGrundstueckEntry: GebaeudeAufGrundstueckDetailEntryMeta): string {
    if (!version) {
      return '';
    }
    if (this.formatElement === 'GEBAEUDE_TYP00') {
      return [this.getArtZusatzSummary(version), this.getFlaeche(version.totalQuadratmeter),
        this.getVersicherungsnummer(version)].filter(val => val).join(', ');
    }
    if (this.formatElement === 'GEBAEUDE_TYP01') {
      return [this.getArtZusatzSummary(version), this.getGrundstueckZuGebFlaeche(version.totalQuadratmeter),
        this.getGesamtFlaeche(version.totalQuadratmeter, gebaeudeAufGrundstueckEntry ? gebaeudeAufGrundstueckEntry.flaecheQuadratmeter : null),
        this.getVersicherungsnummer(version)].filter(val => val).join(', ');
    }
    if (this.formatElement === 'GEBAEUDE_TYP02') {
      return [this.getArtZusatzSummary(version), this.getVersicherungsnummer(version)].filter(val => val).join(', ');
    }
    if (this.formatElement === 'GEBAEUDE_TYP03') {
      return this.getArtZusatz(version);
    }
    if (this.formatElement === 'GEBAEUDE_TYP04') {
      const flaeche = this.getGesamtFlaeche(version.totalQuadratmeter,
        gebaeudeAufGrundstueckEntry ? gebaeudeAufGrundstueckEntry.flaecheQuadratmeter : null);
      let summary = this.getSummary(version);
        if (flaeche?.length > 0 && summary?.length > 0) {
          summary += ",";
        }
      return [this.getArtZusatz(version), summary, flaeche].filter(val => val).join(' ');
    }
    if (this.formatElement === 'GEBAEUDE_TYP05') {
      return [this.getArtZusatz(version), this.getFlaeche(version.totalQuadratmeter), this.getEgid(version)].filter(val => val).join(', ');
    }
    if (this.formatElement === 'GEBAEUDE_TYP06') {
      return [this.getArtZusatz(version), this.getSummary(version)].filter(val => val).join(' ');
    }
    return this.getArtZusatz(version);
  }

  private getVersicherungsnummer(version: GebaeudeVersionDetailEntryMeta): string {
    if (version.versicherungsNummer) {
      return 'Assek. Nr. ' + version.versicherungsNummer;
    }
    return '';
  }

  private getGesamtFlaeche(flaeche: number, grundstZuGebFlaeche: number): string {
    if (flaeche && grundstZuGebFlaeche && flaeche !== grundstZuGebFlaeche) {
      return this.translateService.instant('Gesamtfläche') + ' ' + this.getFlaeche(flaeche);
    }
    if (flaeche) {
      return this.getFlaeche(flaeche);
    }
    return '';
  }

  private getFlaeche(flaeche: number): string {
    if (flaeche) {
      return this.hochkommaPipe.transform(flaeche) + ' m²';
    }
    return '';
  }

  private getGrundstueckZuGebFlaeche(flaeche: number): string {
    if (flaeche) {
      return this.hochkommaPipe.transform(flaeche) + ' m²';
    }
    return '';
  }

  private getArtZusatzSummary(version: GebaeudeVersionDetailEntryMeta) {
    return [this.getArtZusatz(version), this.getSummary(version)].filter(val => val).join(' ');
  }

  private getArtZusatz(version: GebaeudeVersionDetailEntryMeta): string {
    if (version) {
      if (version.gebaeudeArten && version.gebaeudeArten.length > 0) {
        let gebaeudeArten = "";
        for (const art of version.gebaeudeArten) {
          let gebaeudeArtCode = "";
          if (art.code) {
            gebaeudeArtCode = this.translateName.transform(art.code.text);
          }
          const gebaeudeArtCodeAndZusatz = [gebaeudeArtCode, art.zusatz].filter(val => val).join(' ');
          gebaeudeArten = [gebaeudeArten, gebaeudeArtCodeAndZusatz].filter(val => val).join(', ');
        }
        return gebaeudeArten;
      } else {
          return this.translateService.instant("Gebäude/Bauten");
        }
      }
    }

  private getSummary(version: GebaeudeVersionDetailEntryMeta): string {
    let summary = [version.unterirdisch ? this.translateService.instant('unterirdisch') : '', version.projektiert ? this.translateService.instant('projektiert') : '',
      version.einzelobjekt ? this.translateService.instant('Einzelobjekt') : ''].filter(val => val).join(', ');
    if (summary.length > 0) {
      summary = '(' + summary + ')';
    }
    return summary;
  }

  private getEgid(version: GebaeudeVersionDetailEntryMeta): string {
    if (version.gwrEgid) {
      return 'EGID: ' + version.gwrEgid;
    }
    return '';
  }
}
